import { faTimes, faUpload } from '@fortawesome/free-solid-svg-icons'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import { Button } from '@/components'
import { Modal } from '@/components/Modal/Modal'
import { StructureDto, StructureObjectDto } from '@/endpoints/models'
import { useAppContext } from '@/hooks'
import { useScrollbarWidth } from '@/hooks/useScrollbarWidth'
import CsvImporter from '@/pages/User/pages/Home/components/TreePanel/components/CsvImporter/CsvImporter'
import type { CheckResult } from '@/rtkApi/mddApiSliceGeneratedV2'

import { UploadFile } from '../../UploadFile'
import { JsonImportParams, useImport } from '../hooks/useImport'
import { CsvOnMapped } from './CsvImporter/context/types'
import { StructureImportJsonCheck } from './StructureImportJsonCheck'
import { StructureImportPreview } from './StructureImportPreview'

type ImportStructureModalProps = {
	node?: StructureDto,
	onClose: () => void,
	title?: string
}

export const ImportStructureModal = ({
	node,
	onClose,
	title,
}: ImportStructureModalProps) => {
	const { t } = useAppContext()

	const {
		loading,
		handleFile,
		handleImport,
		structure,
		csvData,
		setCsvRows,
		setImportProps,
		file,
		setFile,
		runJsonCheck,
		setJsonImportParams,
		checkJsonLoading,
	} = useImport(onClose, node?.id)

	const [csvMapped, setCsvMapped] = useState<boolean>(false)
	const [jsonCheckResult, setJsonCheckResult] = useState<CheckResult | null>(
		null,
	)

	useEffect(() => {
		setJsonCheckResult(null)
	}, [structure])

	const jsonCheckPassed =
		jsonCheckResult != null && (jsonCheckResult.errors?.length ?? 0) === 0

	const onJsonCheckResult = useCallback(
		(result: CheckResult, params: JsonImportParams) => {
			setJsonCheckResult(result)
			if ((result.errors?.length ?? 0) === 0) {
				setJsonImportParams(params)
			} else {
				setJsonImportParams(null)
			}
		},
		[setJsonImportParams],
	)

	const onMapped = useCallback<CsvOnMapped>(
		(data, importStructureProps, csvMapped) => {
			setCsvMapped(csvMapped ?? false)
			setImportProps(importStructureProps)
			setCsvRows(data)
		},
		[setCsvRows, setImportProps],
	)

	const scrollbarWidth = useScrollbarWidth()

	return (
		<Modal
			open={true}
			onClose={onClose}
			resizable={true}
			maximizeButtonVisible
			contentStyle={{ width: 565, height: 900 }}
			header={title ? title : t('IMPORT_STRUCTURE')}
			bodyStyle={{
				paddingBottom: '55px',
			}}
			footerStyle={{
				position: 'absolute',
				bottom: 0,
				width: `calc(100% - ${scrollbarWidth}px)`,
				boxSizing: 'border-box',
				background: '#fff',
			}}
			footer={
				<>
					<Button
						schema="primary"
						icon={faUpload}
						disabled={
							loading ||
							(!structure && !csvData) ||
							(!!csvData && !csvMapped) ||
							(!!structure && !jsonCheckPassed)
						}
						isLoading={loading}
						onClick={handleImport}
						data-testid="structure-import-button-import"
					>
						{t('IMPORT_BUTTON')}
					</Button>
					<Button schema="transparent" onClick={onClose} icon={faTimes}>
						{t('CANCEL')}
					</Button>
				</>
			}
		>
			<UploadFile
				file={file}
				handleFile={handleFile}
				loading={loading}
				onRemoveFile={() => {
					setFile(null)
					onMapped()
					setJsonCheckResult(null)
				}}
			/>
			{structure && (
				<>
					<StructureImportPreview structure={structure} />
					<StructureImportJsonCheck
						checkResult={jsonCheckResult}
						checkLoading={checkJsonLoading}
						onRunCheck={(params) => runJsonCheck(structure, params)}
						onCheckResult={onJsonCheckResult}
					/>
				</>
			)}
			{csvData && node?.type !== StructureObjectDto.TypeEnum.FOLDER && (
				<ErrorText>{t('CSV_CAN_BE_IMPORTED_ONLY_IN_FOLDER')}</ErrorText>
			)}
			{csvData && node && node?.type === StructureObjectDto.TypeEnum.FOLDER && (
				<CsvImporter
					nodeId={node?.id}
					inputData={csvData}
					onMapped={onMapped}
				/>
			)}
		</Modal>
	)
}

const ErrorText = styled.div`
	color: ${(props) => props.theme.colors.danger.base};
	text-align: center;
`
